import React from 'react';
import Layout from '../../components/Layout';
import Icon from '../../components/AppIcon';
import { Link } from 'react-router-dom';

const CompliancePage = () => {
  const complianceAreas = [
    {
      title: 'KYC / KYB Policies',
      icon: 'UserCheck',
      description: 'Comprehensive identity verification for vendors and businesses',
      items: [
        'Government ID verification',
        'Business registration validation',
        'Address verification',
        'Beneficial ownership identification',
        'Ongoing monitoring and re-verification',
      ],
    },
    {
      title: 'Payment Security',
      icon: 'Shield',
      description: 'PCI DSS compliant payment processing infrastructure',
      items: [
        'PCI DSS Level 1 compliance',
        'Tokenized card storage',
        'End-to-end encryption',
        'Fraud detection systems',
        'Secure payment gateway integration',
      ],
    },
    {
      title: 'Data Privacy',
      icon: 'Lock',
      description: 'Protecting user data across all jurisdictions',
      items: [
        'GDPR-ready infrastructure',
        'Data minimization practices',
        'User consent management',
        'Right to deletion compliance',
        'Privacy by design principles',
      ],
    },
  ];

  const regionalCompliance = [
    {
      region: 'Dominican Republic',
      flag: '🇩🇴',
      requirements: ['DGII tax reporting', 'Consumer protection laws', 'Financial services regulation', 'Data localization requirements'],
    },
    {
      region: 'United States',
      flag: '🇺🇸',
      requirements: ['State-by-state licensing', 'AML/BSA compliance', 'CCPA privacy requirements', 'Federal trade regulations'],
    },
    {
      region: 'Puerto Rico',
      flag: '🇵🇷',
      requirements: ['Local business licensing', 'US federal requirements', 'Puerto Rico tax code', 'Consumer protection'],
    },
  ];

  const certifications = [
    { name: 'SOC 2 Type II', status: 'In Progress', description: 'Security and availability controls audit' },
    { name: 'PCI DSS', status: 'Compliant', description: 'Payment card industry data security' },
    { name: 'ISO 27001', status: 'Planned', description: 'Information security management' },
  ];

  return (
    <Layout>
      {/* Hero Section */}
      <section className="py-20 bg-white">
        <div className="max-w-wide mx-auto px-4 lg:px-8">
          <div className="max-w-3xl">
            <p className="text-sm font-medium text-accent uppercase tracking-wider mb-4">
              Regulation & Compliance
            </p>
            <h1 className="text-hero text-foreground mb-6">
              Building Trust Through Compliance
            </h1>
            <p className="text-body-large">
              We maintain the highest standards of regulatory compliance across all
              markets we operate in, ensuring trust and security for all platform participants.
            </p>
          </div>
        </div>
      </section>

      {/* Compliance Areas */}
      <section className="py-20 bg-muted">
        <div className="max-w-wide mx-auto px-4 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-8">
            {complianceAreas.map((area, index) => (
              <div key={index} className="bg-white rounded-brand-lg p-8 border border-border">
                <div className="w-14 h-14 bg-primary/10 rounded-brand flex items-center justify-center mb-6">
                  <Icon name={area.icon} size={28} className="text-primary" />
                </div>
                <h3 className="text-xl font-semibold text-foreground mb-2">
                  {area.title}
                </h3>
                <p className="text-muted-foreground mb-6">
                  {area.description}
                </p>
                <ul className="space-y-3">
                  {area.items.map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-foreground">
                      <Icon name="Check" size={14} className="text-accent mt-0.5" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Regional Compliance */}
      <section className="py-20 bg-white">
        <div className="max-w-wide mx-auto px-4 lg:px-8">
          <div className="mb-12">
            <h2 className="text-display text-foreground mb-4">
              Regional Compliance
            </h2>
            <p className="text-muted-foreground max-w-2xl">
              We maintain compliance with local regulations in every market we operate.
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-6">
            {regionalCompliance.map((region, index) => (
              <div key={index} className="bg-muted rounded-brand p-6">
                <div className="flex items-center gap-3 mb-6">
                  <span className="text-3xl">{region.flag}</span>
                  <h3 className="text-lg font-semibold text-foreground">{region.region}</h3>
                </div>
                <ul className="space-y-3">
                  {region.requirements.map((req, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-foreground">
                      <Icon name="Check" size={14} className="text-accent" />
                      {req}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Security Certifications */}
      <section id="security" className="py-20 bg-primary">
        <div className="max-w-wide mx-auto px-4 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-display text-white mb-4">
              Security Certifications
            </h2>
            <p className="text-white/70 max-w-2xl mx-auto">
              Our commitment to security is validated through industry-recognized certifications.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {certifications.map((cert, index) => (
              <div key={index} className="bg-white/10 backdrop-blur rounded-brand p-6 text-center">
                <div className="w-16 h-16 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon name="Shield" size={32} className="text-white" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">{cert.name}</h3>
                <p className="text-sm text-white/70 mb-4">{cert.description}</p>
                <span className={`inline-flex px-3 py-1 text-xs font-medium rounded-full ${
                  cert.status === 'Compliant' ? 'bg-accent text-white' :
                  cert.status === 'In Progress' ? 'bg-secondary text-white' :
                  'bg-white/20 text-white'
                }`}>
                  {cert.status}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Additional Compliance Topics */}
      <section className="py-20 bg-white">
        <div className="max-w-wide mx-auto px-4 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-8">
            {/* Tax Handling */}
            <div className="bg-muted rounded-brand p-8">
              <Icon name="Receipt" size={28} className="text-primary mb-4" />
              <h3 className="text-xl font-semibold text-foreground mb-4">
                Tax Handling
              </h3>
              <p className="text-muted-foreground mb-6">
                We provide tools and reporting to help vendors maintain tax compliance.
              </p>
              <ul className="space-y-3">
                <li className="flex items-center gap-2 text-sm text-foreground">
                  <Icon name="Check" size={14} className="text-accent" />
                  Digital receipt generation
                </li>
                <li className="flex items-center gap-2 text-sm text-foreground">
                  <Icon name="Check" size={14} className="text-accent" />
                  Tax reporting exports
                </li>
                <li className="flex items-center gap-2 text-sm text-foreground">
                  <Icon name="Check" size={14} className="text-accent" />
                  Income tracking dashboards
                </li>
                <li className="flex items-center gap-2 text-sm text-foreground">
                  <Icon name="Check" size={14} className="text-accent" />
                  Government registry integration
                </li>
              </ul>
            </div>

            {/* Dispute Resolution */}
            <div className="bg-muted rounded-brand p-8">
              <Icon name="Scale" size={28} className="text-primary mb-4" />
              <h3 className="text-xl font-semibold text-foreground mb-4">
                Dispute Resolution
              </h3>
              <p className="text-muted-foreground mb-6">
                Clear processes for handling disputes between platform participants.
              </p>
              <ul className="space-y-3">
                <li className="flex items-center gap-2 text-sm text-foreground">
                  <Icon name="Check" size={14} className="text-accent" />
                  Structured escalation pathway
                </li>
                <li className="flex items-center gap-2 text-sm text-foreground">
                  <Icon name="Check" size={14} className="text-accent" />
                  Mediation services
                </li>
                <li className="flex items-center gap-2 text-sm text-foreground">
                  <Icon name="Check" size={14} className="text-accent" />
                  Refund and chargeback handling
                </li>
                <li className="flex items-center gap-2 text-sm text-foreground">
                  <Icon name="Check" size={14} className="text-accent" />
                  Clear liability framework
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Policies */}
      <section id="privacy" className="py-20 bg-muted">
        <div className="max-w-wide mx-auto px-4 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-display text-foreground mb-4">
              Legal Policies
            </h2>
            <p className="text-muted-foreground">
              Review our legal documents and policies governing platform use.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <a href="#" className="bg-white rounded-brand p-6 border border-border hover:border-primary/20 hover:shadow-corporate-md transition-all duration-300">
              <Icon name="FileText" size={24} className="text-primary mb-4" />
              <h3 className="font-semibold text-foreground mb-2">Privacy Policy</h3>
              <p className="text-sm text-muted-foreground">How we collect, use, and protect your data.</p>
            </a>
            <a href="#" className="bg-white rounded-brand p-6 border border-border hover:border-primary/20 hover:shadow-corporate-md transition-all duration-300">
              <Icon name="FileText" size={24} className="text-primary mb-4" />
              <h3 className="font-semibold text-foreground mb-2">Terms of Service</h3>
              <p className="text-sm text-muted-foreground">Rules and guidelines for using our platform.</p>
            </a>
            <a href="#" className="bg-white rounded-brand p-6 border border-border hover:border-primary/20 hover:shadow-corporate-md transition-all duration-300">
              <Icon name="FileText" size={24} className="text-primary mb-4" />
              <h3 className="font-semibold text-foreground mb-2">Cookie Policy</h3>
              <p className="text-sm text-muted-foreground">How we use cookies and tracking technologies.</p>
            </a>
            <a href="#" className="bg-white rounded-brand p-6 border border-border hover:border-primary/20 hover:shadow-corporate-md transition-all duration-300">
              <Icon name="FileText" size={24} className="text-primary mb-4" />
              <h3 className="font-semibold text-foreground mb-2">Vendor Agreement</h3>
              <p className="text-sm text-muted-foreground">Terms for service providers on our platform.</p>
            </a>
          </div>
        </div>
      </section>

      {/* Contact */}
      <section className="py-20 bg-white">
        <div className="max-w-wide mx-auto px-4 lg:px-8 text-center">
          <h2 className="text-display text-foreground mb-6">
            Compliance Questions?
          </h2>
          <p className="text-muted-foreground mb-8 max-w-2xl mx-auto">
            Our compliance team is available to answer questions about our policies,
            certifications, and regulatory approach.
          </p>
          <a
            href="mailto:compliance@unitedempresaas.com"
            className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-white font-medium rounded-brand-sm hover:bg-primary-light transition-colors"
          >
            <Icon name="Mail" size={16} />
            compliance@unitedempresaas.com
          </a>
        </div>
      </section>
    </Layout>
  );
};

export default CompliancePage;
